<script>
    import { onMount } from "svelte";

    import { fly } from "svelte/transition";

    import { apiMethods } from "../../Services/index.svelte";

    import { isMobile, user } from "../../stores";

    import {
        SearchInput,
        SvelteTable,
        PageLoading,
        Button,
        LoadingModal,
        ResetFilterLink,
        DoctorMultiSearchSelect,
        UploadPdfModal,
    } from "../../Components/Common/index.svelte";

    import {
        getAmountInCurrency,
        getFormatDate,
        getFormatDateAndTime,
        getText,
        getTitle,
        goToURL,
        replaceURL,
        handleDownloadPDF,
        showError,
        decodeSearchParams,
        encodeSearchParams,
    } from "../../Utils/utilFunctions.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";
    import DefaultDoctorFilter from "../../Components/Common/DefaultDoctorFilter.svelte";
    import SearchDateRangeInput from "../../Components/Common/SearchDateRangeInput.svelte";

    export let params = {};

    $: isUserEnableSeeDoctorFilter = $user.role == 100 || $user.role == 20;

    let isGettingAccountSchedules = false,
        returnAccountSchedules = [];

    let filters = {},
        filterDoctorObj,
        showFilters = false;

    let isDownloadingPDF = false,
        isUploadingFileModalOpen = false;

    let selectedDoctors = [];

    let page = 0,
        pageSize = 25,
        count;

    const defaultColumnClass = "text-left pl-4 text-[14px] ";

    const columns = [
        {
            key: "createdDate",
            title: "Created Date",
            headerClass: "text-left pl-4 text-[14px] w-[150px] rounded-tl-xl",
            class: (v) => defaultColumnClass + "w-[150px] cursor-default",
            renderValue: (v) => getFormatDateAndTime(v.createdDate),
        },
        {
            key: "doctorName",
            title: "Doctor",
            value: (v) => getText(v.accountName),
            headerClass: "text-left pl-4 text-[14px] w-[250px]",
            class: (v) =>
                defaultColumnClass + "w-[250px] cursor-pointer hover:underline",
        },
        {
            key: "encounterImported",
            title: "Encounters Imported",
            value: (v) => _renderEncountersImported(v.encounterIds),
            csvContent: (v) => _renderEncountersImported(v.encounterIds, true),
            headerClass: "text-left pl-4 text-[14px]",
            class: (v) => defaultColumnClass + "cursor-default",
        },
        {
            key: "actions",
            title: "Actions",
            headerClass: "text-left px-3 cursor-default rounded-tr-xl",
            class: "text-left px-3 cursor-pointer w-[150px]",
            renderValue: (v) => _renderActionIcons(),
            csvExportHidden: true,
        },
    ];

    onMount(async () => {
        if (params.query) await updateSearchParamsByQuery(params.query);

        loadAccountSchedules(page * pageSize, pageSize, _getSearchParams());
    });

    const updateSearchParamsByQuery = async (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1];

                if (isUserEnableSeeDoctorFilter) {
                    if (arr[i].includes("doctorId=")) {
                        if (isNaN(arr[i].split("=")[1])) {
                            filters.doctorId = arr[i].split("=")[1];
                            selectedDoctors = filters.doctorId
                                .split(",")
                                .map((id) => Number(id));
                        } else {
                            filters.doctorId = Number(arr[i].split("=")[1]);
                            selectedDoctors = [Number(filters.doctorId)];
                        }
                    }
                }

                if (arr[i].includes("startDate="))
                    filters.startDate = arr[i].split("=")[1];

                if (arr[i].includes("endDate="))
                    filters.endDate = arr[i].split("=")[1];
            }
            updateURL();
        }
    };

    const loadAccountSchedules = async (start, limit, params) => {
        isGettingAccountSchedules = true;

        await apiMethods.getMethod
            .getAccountSchedules(start, limit, params)
            .then((res) => _handleAccountSchedules(res));

        isGettingAccountSchedules = false;
    };

    const downloadSchedulePDF = async (id) => {
        isDownloadingPDF = true;

        await apiMethods.getMethod
            .getAccountSchedulePDF(id)
            .then((res) => _handleDownloadSchedulePDF(res));

        isDownloadingPDF = false;
    };

    const afterUploadPDF = () => {
        loadAccountSchedules(page * pageSize, pageSize, _getSearchParams());
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadAccountSchedules(0, pageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/AccountSchedules/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const toggleFilters = () => {
        showFilters = !showFilters;
    };

    const filterTransition = (node, args) => fly(node, args);

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadAccountSchedules(0, newPageSize, _getSearchParams());
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadAccountSchedules(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const handleClickCell = (
        { target: { className, classList } },
        rowData,
        key,
    ) => {
        if (key == "doctorName") goToURL(`/Account/view/${rowData.accountId}`);
        else if (key == "actions") {
            if (className.includes("downloadIcon"))
                downloadSchedulePDF(rowData.id);
        } else if (key == "encounterImported") {
            const { encounterIds } = rowData;
            if (encounterIds.length) {
                const cList = className.split(" ");
                const eID = cList.find((c) =>
                    c.toString().includes("encounter"),
                );
                if (eID)
                    goToURL(
                        `/PatientEncounter/view/${eID.replace(
                            "encounter",
                            "",
                        )}`,
                    );
            }
        }
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    const handleSelectDoctor = () => {
        filters.doctorId = selectedDoctors.join(",");

        handleFilterSelect();
    };

    const clearFilterDoctor = () => {
        selectedDoctors = [];

        delete filters.doctorId;

        handleFilterSelect();
    };
    // HELPER FUNCTIONS
    const _handleAccountSchedules = async (res) => {
        if (res.error) {
            showError(res.error);
            returnAccountSchedules = [];
            count = 0;
        } else {
            returnAccountSchedules = [...res.results];

            count = res.total;
        }
    };

    const _handleDownloadSchedulePDF = (res) => {
        if (!res.pdfContent)
            showError(
                "Failed to load the schedule PDF content, please refresh the page and try again and contact support if the issue persists.",
            );
        else handleDownloadPDF(res.pdfContent, "Account Schedule", true);
    };

    const _getSearchParams = () => {
        let params = { sort: "createddateDesc" };

        if (filters.sort) params.sort = filters.sort;

        if (isUserEnableSeeDoctorFilter) {
            if (filters.doctorId) params["filter[doctorId]"] = filters.doctorId;
        }

        if (filters.startDate) params["filter[startdate]"] = filters.startDate;

        if (filters.endDate) params["filter[enddate]"] = filters.endDate;

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.sort) str += `&sort=${params.sort}`;

            if (isUserEnableSeeDoctorFilter) {
                if (params["filter[doctorId]"])
                    str += `&doctorId=${params["filter[doctorId]"]}`;
            }

            if (params["filter[startdate]"])
                str += `&startDate=${params["filter[startdate]"]}`;

            if (params["filter[enddate]"])
                str += `&endDate=${params["filter[enddate]"]}`;
        }

        return encodeSearchParams(str);
    };

    const _renderEncountersImported = (arr, isCsvContent = false) => {
        if (!arr.length) return "-";

        arr = arr.map((eID) =>
            isCsvContent
                ? `${eID}`
                : `<span class="cursor-pointer hover:underline encounter${eID}">${eID}</span>`,
        );

        return arr.join(isCsvContent ? "\r\n" : ", ");
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="downloadIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Download Schedule PDF">
                        <i class="downloadIcon fas fa-download"></i>
                    </div>
                </div>`;
    };

    const _getFullName = (v) => {
        return `${getTitle(v.title)} ${v.fullName}`;
    };

    const _updateCSVContent = (arr) => {
        if (!arr.length) return [];

        const results = [];

        arr.map((result) => {
            results.push({
                "Doctor ID": getText(result.id),
                Name: _getFullName(result),
                "Account Created": result.accountCreatedDate
                    ? getFormatDate(result.accountCreatedDate)
                    : "-",
                "No. of Encounters": result.totalEncounters
                    ? getText(result.totalEncounters)
                    : "0",
                "Total Billed": getAmountInCurrency(
                    result.totalBilledAmount,
                ).replaceAll(",", ""),
                "Total Collected": getAmountInCurrency(
                    result.totalCollectedAmount,
                ).replaceAll(",", ""),
                "Total Refunded": getAmountInCurrency(
                    result.totalRefundedAmount,
                ).replaceAll(",", ""),
                "Last Interaction": getFormatDateAndTime(
                    result.lastInteraction,
                ).replace(",", " "),
            });
        });

        return results;
    };

    const _openPostPdfModal = () => (isUploadingFileModalOpen = true);

    const _closePostPdfModal = () => (isUploadingFileModalOpen = false);
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-32 flex flex-row gap-x-1"
            on:click|preventDefault={toggleFilters}
        >
            Filters
            <i class="fa fa-plus mr-2 text-[14px]" aria-hidden="true" />
            <i
                style="font-size:14px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>
    {/if}

    {#if !$isMobile || showFilters}
        <div
            class="flex flex-0 items-start {$isMobile
                ? 'flex-col-reverse gap-y-4'
                : 'justify-between'}"
            in:filterTransition={{ duration: 200, y: -50 }}
        >
            <div class="flex gap-4 flex-wrap items-center">
                {#if isUserEnableSeeDoctorFilter}
                    <DefaultDoctorFilter
                        bind:filter={filters}
                        filterPropertyName={"doctorId"}
                        onChangeFunc={() => {
                            selectedDoctors = filters["doctorId"].split(",");
                            handleFilterSelect();
                        }}
                    />

                    <DoctorMultiSearchSelect
                        bind:searchResult={selectedDoctors}
                        onChangeFunc={handleSelectDoctor}
                        inputClass="w-32"
                        zIndex="3"
                    />
                {/if}

                {#if !$user.settings?.disableDateRangeInput}
                    <SearchDateRangeInput
                        bind:startDate={filters.startDate}
                        bind:endDate={filters.endDate}
                        title="Date Range"
                        inputClass="borderB w-[272px]"
                        on:change={handleFilterSelect}
                        showClearButton
                        disabled={isGettingAccountSchedules}
                        showScrollYearOptions
                    />
                {:else}
                    <SearchInput
                        bind:value={filters.startDate}
                        title="Date from"
                        type="date"
                        isLong
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingAccountSchedules}
                        maxDate={filters.endDate ?? undefined}
                        showScrollYearOptions
                    />

                    <SearchInput
                        bind:value={filters.endDate}
                        title="Date to"
                        type="date"
                        isLong
                        inputClass="borderB"
                        inputFunc={handleFilterSelect}
                        isDisable={isGettingAccountSchedules}
                        minDate={filters.startDate ?? undefined}
                        showScrollYearOptions
                    />
                {/if}

                <ResetFilterLink
                    bind:filter={filters}
                    defaultFilter={{ sort: "createddateDesc" }}
                    onResetFilter={() => {
                        clearFilterDoctor();
                    }}
                />
            </div>

            <Button clickFunc={_openPostPdfModal}>Upload List PDF</Button>
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingAccountSchedules}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    {columns}
                    rows={returnAccountSchedules}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnAccountSchedules}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnAccountSchedules,
            fileName: "Lists",
            allDataApi: async () =>
                await apiMethods.getMethod.getAccountSchedules(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

{#if isDownloadingPDF}
    <LoadingModal titleText="Downloading Schedule PDF..." />
{/if}

<UploadPdfModal
    isModalOpen={isUploadingFileModalOpen}
    showDoctorSelector
    showDateInput
    showSurgeonSelector
    closeModal={_closePostPdfModal}
    onUploaded={afterUploadPDF}
/>
